import { useEffect, useState, useCallback } from "react";
import api, { fmtEUR, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Plus, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { EditTxDialog } from "@/components/bancos/EditTxDialog";
import { AddExpenseDialog } from "@/components/bancos/AddExpenseDialog";
import { NewAccountDialog } from "@/components/bancos/NewAccountDialog";
import { DeleteTxDialog } from "@/components/bancos/DeleteTxDialog";
import { InitialBalanceDialog } from "@/components/bancos/InitialBalanceDialog";
import { AccountsCard } from "@/components/bancos/AccountsCard";
import { TransactionsCard } from "@/components/bancos/TransactionsCard";

export default function Bancos() {
  const { org } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState({ total: 0, transactions: [], kpis: {} });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ account_id: "all", date_from: "", date_to: "", q: "", recon: "all" });
  const [showNew, setShowNew] = useState(false);
  const [showExpense, setShowExpense] = useState(false);
  const [accForm, setAccForm] = useState({ name: "", bank: "", iban: "", branch_id: "", kind: "cuenta" });
  const [editTx, setEditTx] = useState(null);
  const [delTx, setDelTx] = useState(null);
  const [balForm, setBalForm] = useState(null);

  const load = useCallback(() => {
    api.get("/banks/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    const p = new URLSearchParams({ page: String(page), limit: "25" });
    if (filters.account_id !== "all") p.set("account_id", filters.account_id);
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    if (filters.q) p.set("q", filters.q);
    if (filters.recon !== "all") p.set("recon", filters.recon);
    api.get(`/banks/transactions?${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const applyFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };

  const createAccount = async () => {
    try {
      await api.post("/banks/accounts", { ...accForm, branch_id: accForm.branch_id || null });
      toast.success("Cuenta creada");
      setShowNew(false);
      setAccForm({ name: "", bank: "", iban: "", branch_id: "", kind: "cuenta" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cuenta");
    }
  };

  const changeKind = async (accountId, kind) => {
    try {
      await api.patch(`/banks/accounts/${accountId}`, { kind });
      toast.success("Tipo de cuenta actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    }
  };

  const saveTx = async (payload) => {
    try {
      await api.patch(`/banks/transactions/${editTx.id}`, payload);
      toast.success("Movimiento actualizado");
      setEditTx(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    }
  };

  const accKind = (id) => accounts.find((a) => a.id === id)?.kind;

  const saveInitialBalance = async () => {
    try {
      await api.patch(`/banks/accounts/${balForm.id}`, {
        initial_balance: Number(balForm.initial_balance),
        initial_balance_date: balForm.initial_balance_date,
      });
      toast.success("Saldo inicial guardado");
      setBalForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const kpis = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="bancos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Bancos</h1>
          <p className="text-sm text-slate-500 mt-1">Extractos CSV/XLSX/XLS · antiduplicados por hash y por fecha+importe · ingresos positivos, cargos negativos</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-expense-button" onClick={() => setShowExpense(true)} variant="outline" className="gap-2">
            <MinusCircle className="w-4 h-4" /> Añadir gasto
          </Button>
          <Button data-testid="new-account-button" onClick={() => setShowNew(true)} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
            <Plus className="w-4 h-4" /> Nueva cuenta
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="kpi-balance">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">Saldo total</CardTitle>
            <Building2 className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent><div className="text-3xl font-extrabold font-num tracking-tight">{kpis.balance !== null && kpis.balance !== undefined ? fmtEUR(kpis.balance) : "—"}</div></CardContent>
        </Card>
        <Card data-testid="kpi-pending-count">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">Sin conciliar</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-extrabold font-num tracking-tight">{fmtNum(kpis.pending_count ?? 0)}</div></CardContent>
        </Card>
        <Card data-testid="kpi-pending-amount">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">Importe sin conciliar</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-extrabold font-num tracking-tight">{fmtEUR(kpis.pending_amount ?? 0)}</div></CardContent>
        </Card>
      </div>

      <AccountsCard accounts={accounts} onSelectAccount={(id) => applyFilter({ account_id: id })}
        onChangeKind={changeKind}
        onOpenBalance={(a) => setBalForm({ id: a.id, initial_balance: a.initial_balance ?? "", initial_balance_date: a.initial_balance_date || "" })}
        onImported={load} />

      <TransactionsCard data={data} filters={filters} accounts={accounts} page={page} setPage={setPage}
        onFilter={applyFilter} accKind={accKind} onEdit={setEditTx} onDelete={setDelTx} />

      <NewAccountDialog open={showNew} onOpenChange={setShowNew} form={accForm} setForm={setAccForm}
        branches={org.branches} onSave={createAccount} />
      <AddExpenseDialog open={showExpense} onOpenChange={setShowExpense} accounts={accounts} onSaved={load} />
      <EditTxDialog tx={editTx} open={!!editTx} onClose={() => setEditTx(null)} onSave={saveTx} />
      <DeleteTxDialog tx={delTx} onClose={() => setDelTx(null)} onDeleted={load} />
      <InitialBalanceDialog balForm={balForm} setBalForm={setBalForm} onSave={saveInitialBalance} />
    </div>
  );
}
